import React, { useState, useEffect, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import {
  ExclamationCircleIcon,
  ShieldCheckIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { setup2FA, confirm2FA } from "../../crud/auth.crud";

/**
 * Mandatory enrollment wizard for users who do not yet have an authenticator
 * enrolled. Fetches a fresh TOTP secret + provisioning URI, renders a QR code
 * (and a copyable manual-entry key), then confirms the first code to enable 2FA
 * and complete login.
 */
export default function TwoFactorEnroll({ tempToken, onAuthenticated, onCancel }) {
  const [secret, setSecret] = useState("");
  const [otpauthUri, setOtpauthUri] = useState("");
  const [code, setCode] = useState("");
  const [loadingSetup, setLoadingSetup] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fatal, setFatal] = useState(false); // setup failed / session expired
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    let active = true;
    setup2FA(tempToken)
      .then(({ data }) => {
        if (!active) return;
        setLoadingSetup(false);
        if (data && data.errors) {
          setError(data.message || "Unable to start enrollment.");
          setFatal(true);
          return;
        }
        setSecret(data.result.secret);
        setOtpauthUri(data.result.otpauth_uri);
      })
      .catch(() => {
        if (!active) return;
        setLoadingSetup(false);
        setError("Your session expired. Please sign in again.");
        setFatal(true);
      });
    return () => {
      active = false;
    };
  }, [tempToken]);

  const handleCodeChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    if (error) setError("");
  };

  const copySecret = () => {
    if (!secret) return;
    try {
      navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Clipboard not available; user can still read the key.
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (code.length !== 6 || submitting) return;
    setSubmitting(true);
    setError("");

    confirm2FA(tempToken, code)
      .then(({ data }) => {
        setSubmitting(false);
        if (data && data.errors) {
          setCode("");
          setError(data.message || "Invalid code. Please try again.");
          return;
        }
        onAuthenticated(data.result);
      })
      .catch((err) => {
        setSubmitting(false);
        setCode("");
        const status = err && err.response && err.response.status;
        if (status === 429) {
          setError("Too many attempts. Please wait a few minutes and try again.");
        } else if (status === 401 || status === 400) {
          const msg = err.response.data && err.response.data.message;
          // A 401 here usually means the MFA-pending token expired.
          if (msg && /expired|session/i.test(msg)) {
            setError("Your session expired. Please sign in again.");
            setFatal(true);
          } else {
            setError(msg || "Invalid code. Please try again.");
          }
        } else {
          setError("Something went wrong. Please try again.");
        }
      });
  };

  return (
    <div className="tw-space-y-6">
      <div className="tw-text-center">
        <div className="tw-mx-auto tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-100 dark:tw-bg-blue-900/40">
          <ShieldCheckIcon className="tw-h-6 tw-w-6 tw-text-blue-600 dark:tw-text-blue-400" />
        </div>
        <h2 className="tw-mt-4 tw-text-lg tw-font-semibold tw-text-gray-900 dark:tw-text-white">
          Set Up Two-Factor Authentication
        </h2>
        <p className="tw-mt-1 tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
          Two-factor authentication is required. Scan the QR code with an
          authenticator app (Google Authenticator, Authy, 1Password, etc.).
        </p>
      </div>

      {error && (
        <div className="tw-bg-red-50 dark:tw-bg-red-900/20 tw-border tw-border-red-200 dark:tw-border-red-800 tw-rounded-lg tw-p-4 tw-flex tw-items-start tw-gap-3">
          <ExclamationCircleIcon className="tw-h-5 tw-w-5 tw-text-red-600 dark:tw-text-red-400 tw-flex-shrink-0 tw-mt-0.5" />
          <p className="tw-text-sm tw-text-red-700 dark:tw-text-red-400">{error}</p>
        </div>
      )}

      {loadingSetup && (
        <p className="tw-text-center tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
          Preparing your secure key…
        </p>
      )}

      {fatal && !loadingSetup && (
        <button
          type="button"
          onClick={onCancel}
          className="tw-w-full tw-px-4 tw-py-3 tw-rounded-lg tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-font-semibold tw-transition-all"
        >
          Back to sign in
        </button>
      )}

      {!loadingSetup && !fatal && otpauthUri && (
        <>
          <div className="tw-flex tw-justify-center">
            <div className="tw-bg-white tw-p-3 tw-rounded-lg tw-border tw-border-gray-200">
              <QRCodeCanvas value={otpauthUri} size={180} includeMargin={false} />
            </div>
          </div>

          <div>
            <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mb-1 tw-text-center">
              Can't scan? Enter this key manually:
            </p>
            <button
              type="button"
              onClick={copySecret}
              className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-3 tw-py-2 tw-rounded-lg tw-bg-gray-100 dark:tw-bg-gray-700 tw-text-gray-800 dark:tw-text-gray-100 tw-font-mono tw-text-sm tw-break-all hover:tw-bg-gray-200 dark:hover:tw-bg-gray-600 tw-transition-colors"
            >
              {secret}
              {copied ? (
                <CheckIcon className="tw-h-4 tw-w-4 tw-text-green-600 tw-flex-shrink-0" />
              ) : (
                <ClipboardDocumentIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />
              )}
            </button>
          </div>

          <form onSubmit={submit} className="tw-space-y-4">
            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2 tw-text-center">
                Enter the 6-digit code to confirm
              </label>
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                name="code"
                value={code}
                onChange={handleCodeChange}
                placeholder="000000"
                className="tw-block tw-w-full tw-text-center tw-tracking-[0.5em] tw-text-2xl tw-font-semibold tw-py-3 tw-border tw-rounded-lg tw-bg-white dark:tw-bg-gray-700 tw-text-gray-900 dark:tw-text-white placeholder:tw-text-gray-300 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-border-gray-300 dark:tw-border-gray-600"
              />
            </div>

            <button
              type="submit"
              disabled={submitting || code.length !== 6}
              className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-3 tw-rounded-lg tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-font-semibold tw-shadow-lg hover:tw-shadow-xl tw-transition-all disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
              {submitting ? "Verifying..." : "Enable & Continue"}
            </button>

            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="tw-w-full tw-text-sm tw-font-medium tw-text-gray-500 hover:tw-text-gray-700 dark:tw-text-gray-400 dark:hover:tw-text-gray-200 tw-transition-colors"
              >
                Back to sign in
              </button>
            )}
          </form>
        </>
      )}
    </div>
  );
}
